import { AudioLinesIcon, DownloadIcon, FileIcon } from "lucide-react";
import {
  createFileRoute,
  redirect,
  useBlocker,
  useLocation,
} from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AudioAmplitudeVisualizer } from "./-components/AudioAmplitudeVisualizer";
import AnalyzeRange from "./-components/AnalyzeRange";
import SimilarSpeakers from "./-components/SimilarSpeakers";
import type { Voice } from "@/lib/types/vexon";
import { similaritySearchQueryOptions } from "@/lib/queries/vexon/similaritySearch";
import { AudioPlayer } from "@/components/ui/audio-player";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export const Route = createFileRoute("/_app/vexon/result")({
  component: VexonResult,
  beforeLoad({ location: { state } }) {
    if (!state.vexonResult || !state.vexonAudio)
      throw redirect({ to: "/vexon" });
    else {
      // recreate object urls in case the current one is invalid (for example after reload)
      if (state.vexonAudio.reference) {
        URL.revokeObjectURL(state.vexonAudio.reference.url);
        state.vexonAudio.reference.url = URL.createObjectURL(
          state.vexonAudio.reference.file,
        );
      }
      URL.revokeObjectURL(state.vexonAudio.verify.url);
      state.vexonAudio.verify.url = URL.createObjectURL(
        state.vexonAudio.verify.file,
      );
    }
  },
});

function VexonResult() {
  const navigate = Route.useNavigate();
  const {
    state: { vexonResult, vexonAudio },
  } = useLocation();

  // invalidate on leave without actually blocking
  useBlocker({
    shouldBlockFn() {
      URL.revokeObjectURL(vexonAudio?.reference?.url ?? "");
      URL.revokeObjectURL(vexonAudio?.verify.url ?? "");
      return false;
    },
    enableBeforeUnload: false,
  });

  const { data: similaritySearch } = useQuery({
    ...similaritySearchQueryOptions(vexonResult?.entry_id || ""),
    enabled: !!vexonResult?.entry_id,
  });

  const [playingVoice, setPlayingVoice] = useState<Voice | null>(null);

  const toggleReferencePlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "reference" ? null : "reference";
    });
  };

  const toggleVerifyPlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "verify" ? null : "verify";
    });
  };

  return (
    <div className="w-full rounded-lg border border-gray-200 p-6 dark:border-gray-800">
      <div className="flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Vexon Voice Detector</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Polygraf AI's Voice Verification System
            </p>
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => {
              URL.revokeObjectURL(vexonAudio?.reference?.url ?? "");
              URL.revokeObjectURL(vexonAudio?.verify.url ?? "");
              navigate({ to: "/vexon" });
            }}
          >
            <FileIcon size={16} />
            <span>Start New</span>
          </Button>
        </div>

        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              {vexonAudio!.reference ? (
                <>
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
                    <AudioLinesIcon className="h-5 w-5" />
                    <p className="text-lg font-medium">Reference Voice</p>
                    <Button variant="ghost" size="icon" asChild>
                      <a
                        className="ml-auto justify-self-end"
                        download={vexonAudio!.reference.file.name}
                        href={vexonAudio!.reference.url}
                      >
                        <DownloadIcon className="size-1/2" />
                      </a>
                    </Button>
                  </div>
                  <AudioPlayer
                    audioUrl={vexonAudio!.reference.url}
                    isPlaying={playingVoice === "reference"}
                    onPlayPause={toggleReferencePlayPause}
                  />
                </>
              ) : (
                <Card className="text-center">No Reference Audio Provided</Card>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
                <AudioLinesIcon className="h-5 w-5" />
                <p className="text-lg font-medium">Voice to Verify</p>
                <Button variant="ghost" size="icon" asChild>
                  <a
                    className="ml-auto justify-self-end"
                    download={vexonAudio!.verify.file.name}
                    href={vexonAudio!.verify.url}
                  >
                    <DownloadIcon className="size-1/2" />
                  </a>
                </Button>
              </div>
              <AudioPlayer
                audioUrl={vexonAudio!.verify.url}
                isPlaying={playingVoice === "verify"}
                onPlayPause={toggleVerifyPlayPause}
              />
            </div>
          </div>
          <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />

          <AnalyzeRange data={vexonResult?.results || []} />
          <AudioAmplitudeVisualizer
            fileUrl={vexonAudio!.verify.url}
            data={vexonResult!.results}
          />
          {Object.keys(similaritySearch || {}).length > 0 && (
            <SimilarSpeakers data={similaritySearch || {}} />
          )}
        </div>
      </div>
    </div>
  );
}
